import { useEffect, useMemo, useRef, useState } from 'react'
import { Fade } from '@mui/material'
import { VIEWER_STYLE, MATTERPORT_CONTAINER } from './styles'
import { MpSdk, setupSdk, Model } from '@matterport/sdk'
import useMatterportRoomTracker from '../../Hooks/useMatterportTracker'
import AttributionOverlay from './AttributionOverlay'
import PreparingOverlay from './PreparingOverlay'
import { moveToReel } from '../../MatterportSwap/moveToReel'
import { useModelSettingsQuery } from './modelSettings.generated'

interface ModelViewerProps {
  matterportId: string
  matterportURLParams: { k: string; v: string }[]
}

const ModelViewer = ({ matterportId, matterportURLParams }: ModelViewerProps) => {
  const matterportEl = useRef<HTMLDivElement>(null)
  const [sdk, setSdk] = useState<MpSdk | null>(null)
  const [initialised, setInitialised] = useState<boolean>(false)
  const [modelDetails, setModelDetails] = useState<Model.ModelDetails | null>(null)

  // The reel the visitor actually landed on, once known. Passed to the tracker
  // so the recorded entry point matches where the tour really opened.
  const [openedReelID, setOpenedReelID] = useState<string | null>(null)

  // False until the visitor is standing where the venue chose. Only ever gates
  // the overlay, so a model without an opening view is unaffected.
  const [positioned, setPositioned] = useState(false)

  // Guards a second setupSdk. Creating the SDK twice mounts a second Matterport
  // iframe and emits a duplicate MODEL_VIEW, inflating views against sessions.
  const setupStartedRef = useRef(false)

  const [{ data, fetching }] = useModelSettingsQuery({
    variables: {
      matterportId: matterportId,
    },
  })

  const openingReelID = data?.getModelSettings.openingReelID ?? null

  // A shared ?ss= deep link is an explicit request for a particular view, so it
  // outranks the venue's configured opening reel.
  const hasDeepLink = useMemo(
    () => matterportURLParams.some((p) => p.k === 'ss' && p.v !== ''),
    [matterportURLParams]
  )

  // Only a model with an opening view to apply gets the overlay. Anything else
  // keeps Matterport's own loading screen, which is nicer than a spinner.
  const willReposition = !fetching && !!openingReelID && !hasDeepLink

  const { sendTrackingData } = useMatterportRoomTracker(sdk, matterportId, openedReelID)

  useEffect(() => {
    // Wait for settings before creating the SDK: the opening reel has to be
    // known up front, or the visitor watches the tour jump off the default reel
    // onto the chosen one.
    if (fetching) return
    if (!matterportEl.current || !matterportId) return
    if (setupStartedRef.current) return
    setupStartedRef.current = true

    const setupMSdk = async () => {
      const additionalParams = matterportURLParams.reduce((acc, param) => {
        acc[param.k] = param.v
        return acc
      }, {} as Record<string, string>)

      const matterportSdk = (await setupSdk('4a08zz0ti44ecgcua1m6yt36c', {
        // TODO - get from env/window
        container: matterportEl.current as HTMLDivElement,
        space: matterportId as string,

        // param list available here: https://support.matterport.com/s/article/URL-Parameters?language=en_US
        // note, we can have deep links with ss=xxx and sr=yyy - this links to a specific view/camera point so we need to handle this too.
        iframeQueryParams: { qs: 0, play: 1, mls: 0, brand: 1, ...additionalParams },
      })) as MpSdk

      await matterportSdk.App.state.waitUntil(
        (state) => state.phase === matterportSdk.App.Phase.PLAYING
      )

      // play=1 lands on whichever reel Matterport orders first. If the venue has
      // chosen a different opening view, move to it while the overlay is still
      // covering the model, so the correction is never seen.
      let opened: string | null = null
      if (openingReelID && !hasDeepLink) {
        try {
          const reels = await matterportSdk.Tour.getData()
          const index = reels.findIndex((reel) => reel.sid === openingReelID)
          // A chosen reel can be deleted in Matterport later. If it has gone,
          // leave the default alone rather than guessing at a replacement.
          if (index >= 0) {
            await moveToReel(matterportSdk, reels[index], index)
            opened = openingReelID
          }
        } catch (e) {
          console.error('Could not open the configured reel:', e)
        }
      }
      setOpenedReelID(opened)
      setPositioned(true)

      await matterportSdk.Link.setShareLinkPolicy(matterportSdk.Link.CreationPolicy.REFERRER, {
        includeParams: ['ss', 'sr', 'play', 'qs'],
      })

      setSdk(matterportSdk)
      setInitialised(true)

      const modelDetails = await matterportSdk.Model.getDetails()
      setModelDetails(modelDetails)
    }

    setupMSdk().catch((e) => {
      // Never leave the overlay covering a model that will not arrive.
      console.error('Could not start the Matterport viewer:', e)
      setPositioned(true)
    })
  }, [matterportId, matterportURLParams, fetching, openingReelID, hasDeepLink])

  return (
    <>
      <div id='matterportContainer' style={VIEWER_STYLE}>
        <div style={MATTERPORT_CONTAINER} ref={matterportEl}></div>

        <PreparingOverlay show={willReposition && !positioned} />

        {!fetching && data?.getModelSettings.showEnquiryButtonByDefault && modelDetails && (
          <Fade in={initialised} timeout={800}>
            <div>
              <AttributionOverlay
                modelSettings={data.getModelSettings}
                modelDetails={modelDetails}
                matterportId={matterportId}
                sendTrackingData={sendTrackingData}
              />
            </div>
          </Fade>
        )}
      </div>
    </>
  )
}

export default ModelViewer
